import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, Zap, TrafficCone, MapPin, Plus, ShieldAlert } from "lucide-react";
import CommunityFilterBar from "@/components/CommunityFilterBar";
import { useCommunityFilter } from "@/hooks/useCommunityFilter";
import { alertMatchesInterests } from "@/lib/interests";
import { Sparkles } from "lucide-react";

const KIND_META = {
  security: { label: "Security", icon: ShieldAlert, tint: "text-[#C1292E]", bg: "bg-[#C1292E]/10" },
  utility:  { label: "Utility",  icon: Zap,          tint: "text-[#D95D39]", bg: "bg-[#D95D39]/10" },
  road:     { label: "Road",     icon: TrafficCone,  tint: "text-[#2E473B]", bg: "bg-[#F4A261]/25" },
};

const SEV_COLORS = {
  low:    "bg-emerald-100 text-emerald-800",
  medium: "bg-amber-100 text-amber-800",
  high:   "bg-red-100 text-red-800",
};

function relTime(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ""; }
}

export default function Alerts() {
  const { user } = useAuth();
  const community = useCommunityFilter();
  const [tab, setTab] = useState("all");
  const [alerts, setAlerts] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: "security", title: "", description: "", location: "", severity: "medium",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = community.filterParam ? { community: community.filterParam } : {};
      const { data } = await api.get("/alerts", { params });
      setAlerts(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = "Alerts · Jirani"; }, []);
  useEffect(() => {
    if (!user) { setInterests([]); return; }
    api.get("/preferences").then(({ data }) => setInterests(data.interests || [])).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (community.loading) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community.filterParam, community.loading]);

  const filtered = tab === "all" ? alerts : alerts.filter((a) => a.kind === tab);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/alerts", form);
      setAlerts([data, ...alerts]);
      setOpen(false);
      setForm({ kind: "security", title: "", description: "", location: "", severity: "medium" });
      toast.success("Alert posted. Asante for looking out!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="alerts-page" className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="jirani-eyebrow">Stay safe, stay informed</div>
          <h1 className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
            <AlertTriangle className="h-8 w-8 text-[#D95D39]" /> Alerts
          </h1>
          <p className="mt-2 max-w-xl text-[#4A4A4A]">
            Security, utility outages, and road updates from your neighborhood.
          </p>
          <div className="mt-4">
            <CommunityFilterBar
              active={community.active}
              showAll={community.showAll}
              onToggle={community.setShowAll}
            />
          </div>
        </div>

        {user && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-alert-btn"
                className="jirani-pill rounded-full bg-[#D95D39] px-5 text-white hover:bg-[#c25333]"
              >
                <Plus className="mr-2 h-4 w-4" /> Post alert
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Outfit" }}>Post a neighborhood alert</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Category</Label>
                  <select
                    data-testid="alert-kind-select"
                    value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3"
                  >
                    <option value="security">Security</option>
                    <option value="utility">Utility (power / water)</option>
                    <option value="road">Road</option>
                  </select>
                </div>
                <div>
                  <Label>Severity</Label>
                  <select
                    data-testid="alert-severity-select"
                    value={form.severity}
                    onChange={(e) => setForm({ ...form, severity: e.target.value })}
                    className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <Label>Title</Label>
                  <Input
                    data-testid="alert-title-input"
                    required maxLength={140}
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    data-testid="alert-location-input"
                    required
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="mt-1 h-11 rounded-xl"
                    placeholder="Neighborhood, road, landmark"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    data-testid="alert-description-input"
                    required rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <DialogFooter>
                  <Button
                    data-testid="alert-submit-btn"
                    type="submit"
                    disabled={saving}
                    className="jirani-pill w-full rounded-full bg-[#D95D39] text-white hover:bg-[#c25333]"
                  >
                    {saving ? "Posting..." : "Post alert"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <Tabs value={tab} onValueChange={setTab} data-testid="alerts-tabs">
        <TabsList className="mb-6 rounded-full bg-[#F9F7F4] p-1">
          <TabsTrigger data-testid="tab-all" value="all" className="rounded-full data-[state=active]:bg-white">All</TabsTrigger>
          <TabsTrigger data-testid="tab-security" value="security" className="rounded-full data-[state=active]:bg-white">Security</TabsTrigger>
          <TabsTrigger data-testid="tab-utility" value="utility" className="rounded-full data-[state=active]:bg-white">Utility</TabsTrigger>
          <TabsTrigger data-testid="tab-road" value="road" className="rounded-full data-[state=active]:bg-white">Road</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {loading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-40 animate-pulse rounded-2xl border border-black/5 bg-white/60" />
              ))}
            </div>
          ) : (
            <div data-testid="alerts-list" className="grid gap-4 md:grid-cols-2">
              {filtered.map((a) => {
                const meta = KIND_META[a.kind] || KIND_META.security;
                const Icon = meta.icon;
                const forYou = alertMatchesInterests(a, interests);
                return (
                  <article
                    key={a.id}
                    data-testid={`alert-${a.id}`}
                    className={`jirani-card overflow-hidden rounded-2xl border bg-white p-5 ${
                      forYou ? "border-[#D95D39]/30 ring-1 ring-[#D95D39]/20" : "border-black/5"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${meta.bg} ${meta.tint}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex gap-2">
                        {forYou && (
                          <span
                            data-testid={`for-you-alert-${a.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-[#D95D39] px-2 py-0.5 text-[10px] font-semibold text-white"
                            title="Matches your interests"
                          >
                            <Sparkles className="h-3 w-3" /> For you
                          </span>
                        )}
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEV_COLORS[a.severity]}`}>
                          {a.severity}
                        </span>
                        <Badge className="rounded-full bg-[#F9F7F4] text-[#2E473B] hover:bg-[#F9F7F4]">
                          {meta.label}
                        </Badge>
                      </div>
                    </div>

                    <h3 className="mt-4 text-lg text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
                      {a.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-[#4A4A4A]">{a.description}</p>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-[#2E473B]/80">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" /> {a.location}
                      </span>
                      <span>
                        {a.author?.name || "Neighbor"} · {relTime(a.created_at)}
                      </span>
                    </div>
                  </article>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center text-[#4A4A4A]">
                  No alerts in this category. That's a good thing!
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
